/**
 * orgApi — thin client for the pse-org worker (StudioCore V2 organisation
 * admins: print approval queue, school roster, license pool, activity).
 *
 * WEB copy. Byte-identical to the desktop's services/orgApi.js except for
 * `ingestUrl`, which exists only here: the browser cannot fetch a public R2
 * design cross-origin, so the worker copies it into the approval prefix
 * server-side. Both the requester side AND the org-admin side are used —
 * an admin on a Chromebook approves from the same console the desktop has.
 * Mirrors userDesignsApi's shape: JWT in Authorization, responses are
 * { success, data }, base URL env-driven with a workers.dev fallback (the
 * worker answers on both hostnames forever — see CLAUDE.md domain rules).
 */
// Brightivity: the school fork behind the front door (brightivity.dev/school/<x> = /org/<x>).
import { API_BASE } from '../config.js';
const BASE = `${API_BASE}/school`;

async function orgFetch(token, endpoint, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${BASE}${endpoint.replace(/^\/org(?=\/)/, '')}`, { ...options, headers });
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON error body */ }
  if (!res.ok || !json || !json.success) {
    const err = new Error((json && json.error) || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = json; // e.g. provisioning 422s carry { error, remaining }
    throw err;
  }
  return json.data;
}

const enc = encodeURIComponent;

export const orgApi = {
  // ── Requester (Send Job) ─────────────────────────────────────────
  uploadToken: (token, body) => orgFetch(token, '/org/print-queue/upload-token', { method: 'POST', body: JSON.stringify(body) }),
  // ingestUrl → { imageKey, thumbKey, sizeBytes, contentType }
  // Server-side copy of a public design URL (library/printables/AI posters)
  // into the caller's approval prefix — WEB ONLY (R2 blocks a cross-origin
  // browser fetch; the desktop reads the bytes locally).
  ingestUrl: (token, body) => orgFetch(token, '/org/print-queue/ingest-url', { method: 'POST', body: JSON.stringify(body) }),
  submitJob: (token, body) => orgFetch(token, '/org/print-queue', { method: 'POST', body: JSON.stringify(body) }),
  listMine: (token) => orgFetch(token, '/org/print-queue/mine'),
  cancelJob: (token, id) => orgFetch(token, `/org/print-queue/${enc(id)}/cancel`, { method: 'POST' }),
  // Remove a finished request from the requester's strip (row is preserved server-side).
  dismissJob: (token, id) => orgFetch(token, `/org/print-queue/${enc(id)}/dismiss`, { method: 'POST' }),

  // ── Org admin: queue ─────────────────────────────────────────────
  listQueue: (token, status = 'pending') => orgFetch(token, `/org/print-queue?status=${enc(status)}`),
  jobImage: (token, id) => orgFetch(token, `/org/print-queue/${enc(id)}/image`),
  approve: (token, id) => orgFetch(token, `/org/print-queue/${enc(id)}/approve`, { method: 'POST' }),
  approveGroup: (token, groupId) => orgFetch(token, `/org/print-queue/group/${enc(groupId)}/approve`, { method: 'POST' }),
  reject: (token, id, note) => orgFetch(token, `/org/print-queue/${enc(id)}/reject`, { method: 'POST', body: JSON.stringify({ note: note || undefined }) }),
  rejectGroup: (token, groupId, note) => orgFetch(token, `/org/print-queue/group/${enc(groupId)}/reject`, { method: 'POST', body: JSON.stringify({ note: note || undefined }) }),
  reportPrinted: (token, id, metrics) => orgFetch(token, `/org/print-queue/${enc(id)}/printed`, { method: 'POST', body: JSON.stringify(metrics || {}) }),
  reportPrintFailed: (token, id, error) => orgFetch(token, `/org/print-queue/${enc(id)}/print-failed`, { method: 'POST', body: JSON.stringify({ error: error || null }) }),

  // ── Org admin: users / licenses / activity ───────────────────────
  listUsers: (token) => orgFetch(token, '/org/users'),
  setPrintPermission: (token, userId, canPrint, note) => orgFetch(token, `/org/users/${enc(userId)}/print-permission`, { method: 'POST', body: JSON.stringify({ canPrint, note: note || undefined }) }),
  listLicenses: (token) => orgFetch(token, '/org/licenses'),
  resetLicense: (token, licenseId) => orgFetch(token, `/org/licenses/${enc(licenseId)}/reset`, { method: 'POST' }),
  userActivity: (token, userId, feed = 'prints') => orgFetch(token, `/org/activity/${enc(userId)}?feed=${enc(feed)}`),

  // ── Org admin: provisioning (V2.1 — seats + credit pool) ─────────
  // getProvisioning → { seats:{used,cap,remaining}, credits:{allocated,spent,remaining,poolExists} }
  getProvisioning: (token) => orgFetch(token, '/org/provisioning'),
  // generateLicenses → { keys:[...], generated:n }   (422 {error,remaining} when over the seat cap)
  generateLicenses: (token, quantity) => orgFetch(token, '/org/licenses/generate', { method: 'POST', body: JSON.stringify({ quantity }) }),
  // assignCredits → { userId, amount, newBalance, remaining, alreadyApplied }  (422 when over pool)
  assignCredits: (token, userId, amount, idempotencyKey) => orgFetch(token, `/org/users/${enc(userId)}/credits`, { method: 'POST', body: JSON.stringify({ amount, idempotencyKey }) }),
  // reclaimCredits → { userId, amount, newBalance, remaining, alreadyApplied }  (422 {error,remaining} when over the user's reclaimable balance)
  reclaimCredits: (token, userId, amount, idempotencyKey) => orgFetch(token, `/org/users/${enc(userId)}/credits/reclaim`, { method: 'POST', body: JSON.stringify({ amount, idempotencyKey }) }),
};

export default orgApi;
